import cn from 'classnames'
import gsap from 'gsap'
import get from 'lodash/get'
import PropTypes from 'prop-types'
import ReactDOM from 'react-dom'
import { useCallback, useEffect, useRef } from 'react'
import { scaleLinear } from 'd3-scale'
import { createUseStyles } from '../../helpers/createStyles'
import { theme } from '../../styles/theme'

// Image will pan when user passes is within 2nd <-> Penultimate segments
const PAN_SEGMENTS = 4

const domainForLength = length => [
  length / PAN_SEGMENTS,
  (length / PAN_SEGMENTS) * (PAN_SEGMENTS - 1)
]

const GalleryFullscreen = ({ image, onClose }) => {
  const styles = useStyles()
  const imageRef = useRef()
  const position = useRef({
    x: 0,
    y: 0
  })
  const running = useRef(false)
  const isLandscape = get(image, 'asset.metadata.dimensions.aspectRatio', 1) > 1

  const handleMouseMove = useCallback(event => {
    position.current.x = event.clientX
    position.current.y = event.clientY
  }, [])

  useEffect(() => {
    const render = () => {
      if (
        running.current &&
        imageRef.current &&
        typeof window !== 'undefined'
      ) {
        const scaleX = scaleLinear()
          .domain(domainForLength(window.innerWidth))
          .range([
            0,
            Math.max(imageRef.current.clientWidth - window.innerWidth, 0) * -1
          ])
          .clamp(true)

        const scaleY = scaleLinear()
          .domain(domainForLength(window.innerHeight))
          .range([
            0,
            Math.max(imageRef.current.clientHeight - window.innerHeight, 0) * -1
          ])
          .clamp(true)

        gsap.set(imageRef.current, {
          x: scaleX(position.current.x),
          y: scaleY(position.current.y)
        })

        window.requestAnimationFrame(render)
      }
    }

    if (image) {
      running.current = true
      window.addEventListener('mousemove', handleMouseMove, { passive: true })
      window.requestAnimationFrame(render)
      return () => {
        running.current = false
        window.removeEventListener('mousemove', handleMouseMove)
      }
    }
  }, [image, isLandscape])

  return ReactDOM.createPortal(
    <div
      aria-modal
      role='dialog'
      className={cn(styles.root, [!!image && 'is-open'])}
    >
      <button className={styles.close} onClick={onClose}>
        {!!image && (
          <img
            tabIndex='0'
            ref={imageRef}
            className={cn(
              styles.image,
              isLandscape ? styles.landscape : styles.portrait
            )}
            src={image?.asset.url}
            alt={image?.asset.alt}
          />
        )}
      </button>
    </div>,
    document.body
  )
}

const useStyles = createUseStyles({
  root: `
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    zIndex: ${theme.zIndex.galleryFullScreen};
    opacity: 0;
    pointer-events: none;
    transition: opacity 150ms;

    &.is-open {
      opacity: 1;
      pointer-events: auto;
      background: ${theme.colors.white};
    }
  `,
  close: `
    width: 100%;
    height: 100%;
    padding: 0;
    cursor: none!important;
  `,
  image: `
    values: absolute;
    top: 0;
    left: 0;
  `,
  landscape: `
    bottom: 0;
    height: 100%;
  `,
  portrait: `
    right: 0;
    width: 100%;
  `
})

GalleryFullscreen.propTypes = {
  image: PropTypes.object,
  onClose: PropTypes.func.isRequired
}

export default GalleryFullscreen
